import type {
  ApiCheckerSettingName,
  ApiCheckerSettings,
} from "api/ApiKeySettings";

/**
 * The meaning of an api key's checker settings, defined once for the cabinet
 * API page and the checker page switch (spec 2026-10-20-website-api-settings,
 * Stage 6). Authoritative source: ipscore-api `controllers/checker.py`.
 *
 *  - `mode = 1` -> automated full report: every provider runs, the toggles
 *    are ignored, one combined report.
 *  - `mode = 0` -> custom check: only the toggled providers run, each with
 *    its own result; the paid ones need requests.
 */
export const REPORT_MODE_AUTOMATED = 1;
export const REPORT_MODE_CUSTOM = 0;

export const isAutomatedMode = (mode: ApiCheckerSettings["mode"]): boolean =>
  Boolean(Number(mode));

/** A stored toggle (0/1, true/false, null) as a boolean. */
export const isOn = (value: unknown): boolean => Boolean(Number(value));

/**
 * The checker page's "API Mode Settings" switch. Checked = the per-provider
 * view, which `IpCheckerTabs` requests with `?mode=false` — i.e. the custom
 * check. Unchecked = `?mode=true`, the automated report. Pinned by a test:
 * the switch, the stored setting and the query must never disagree.
 */
export const checkerSwitchFromMode = (mode: ApiCheckerSettings["mode"]): boolean =>
  !isAutomatedMode(mode);

export const modeFromCheckerSwitch = (checked: boolean): number =>
  checked ? REPORT_MODE_CUSTOM : REPORT_MODE_AUTOMATED;

/** The `mode` query parameter `/check-ip` gets for a switch state. */
export const checkIpQueryMode = (checked: boolean): boolean =>
  isAutomatedMode(modeFromCheckerSwitch(checked));

export interface ProviderInfo {
  name: ApiCheckerSettingName;
  label: string;
  /** Uses requests (api `PAID_CHECKERS`). */
  paid: boolean;
}

/**
 * The provider toggles a custom check honours: exactly the api's
 * `LIGHT_MODE_CHECKERS`. Deliberately absent: `IpStack` (a flag with no
 * implementation), `Proxstop` (commented out of dispatch), `BlackList` and
 * `PortList` (`/check-ip` never dispatches them).
 */
export const CUSTOM_CHECK_PROVIDERS: ProviderInfo[] = [
  { name: "IpQualityScore", label: "IPQualityScore", paid: true },
  { name: "IpIntel", label: "IPIntel", paid: true },
  { name: "AbuseIpDb", label: "AbuseIPDB", paid: true },
  { name: "Scamalytics", label: "Scamalytics", paid: true },
  { name: "IpInfo", label: "IPinfo", paid: false },
  { name: "IpData", label: "ipdata", paid: false },
  { name: "IpApi", label: "ip-api", paid: false },
  { name: "Whois", label: "Whois", paid: false },
];

export const IPQS_STRICTNESS_LEVELS = [0, 1, 2, 3];

/** `bl_mode`: 1 = detailed, 0 = fast (tgbot `lib/domain/api.py` `bl_mode_callback`). */
export const BL_MODE_FAST = 0;
export const BL_MODE_DETAILED = 1;
